import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
} from '@angular/core';
import { IconComponent } from '../../atoms/icon/icon.component';

import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { Link } from '../../../shared/interfaces/link.interface';
import { CommonModule, DOCUMENT } from '@angular/common';
import { LinkComponent } from '../../atoms/link/link.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import LinkType from '../../../shared/enums/link-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';

import { Router } from '@angular/router';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { ActionType } from '../../../shared/enums/action-type.enums';

import { Video } from '../../../shared/interfaces/video.interface';
import { ModalComponent } from '../../molecules/modal/modal.component';
import { ModalType } from '../../../shared/enums/modal-type.enums';
import {
  CaasComponent,
  CaasDownloads,
  CaasFormField,
  CaasLink,
  CaasLinkOptional,
} from '../../../shared/interfaces/caas/caas.interface';
import { ImageComponent } from '../../atoms/image/image.component';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CaasRoutesService } from '../../../services/caas-routes.service';

@Component({
  selector: 'hdi-cross-referencing',
  standalone: true,
  imports: [
    IconComponent,
    CommonModule,
    LinkComponent,
    ButtonComponent,
    ModalComponent,
    ImageComponent,
    CapitalizePipe,
  ],
  templateUrl: './cross-referencing.component.html',
  styleUrls: ['./cross-referencing.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class CrossReferencingComponent implements OnChanges {
  @Input() caasData?: CaasComponent;
  @Input() viewport: ViewportType = ViewportType.desktop;
  @Input() actionsType: ActionType = ActionType.LINK;
  @Input() headline: string =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';
  @Input() innerHTMLText: string =
    '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</p>';
  @Input() actionArray: Link[] = [];
  @Input() buttonLayout: ButtonType | LinkType = ButtonType.PRIMARY;
  @Input() imageSrc?: string;
  @Input() imageAlt?: string;
  @Input() imageTitle?: string;
  @Input() backgroundBannerIcon: string = 'green';
  @Input() bannerIconPrefix: string = 'fal';
  @Input() bannerIconName: string = 'user-lock';
  @Input() backgroundColor?: Colors;
  @Input() isSectionBg!: boolean;
  @Input() reverse: boolean = false;
  @Input() buttonIconName: string = '';
  @Input() showModal: boolean = false;
  @Input() video?: Video;
  Colors = Colors;
  Sizes = Sizes;
  HrefType = HrefType;
  ModalType = ModalType;
  LinkType = LinkType;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private caasBodytextService: CaasBodytextService,
    private cdr: ChangeDetectorRef,
    private imagesService: ImagesService,
    private sanitizer: DomSanitizer,
    private caasRoutesService: CaasRoutesService
  ) {}

  get linkType(): LinkType {
    return this.buttonLayout as LinkType;
  }

  getOptionalLinkType(link: Link): LinkType {
    return link.buttonLayout as LinkType;
  }

  get buttonType(): ButtonType {
    return this.buttonLayout as ButtonType;
  }

  ngOnChanges(): void {
    if (this.caasData) {
      this.setCaasContent(this.caasData);
    }
    this.cdr.detectChanges();
  }

  openModalVideo(video: Video): void {
    this.video = video;
    this.showModal = true;
  }

  setCaasContent(caasData: CaasComponent) {
    this.backgroundColor = this.setBackgroundColor(
      caasData.formData.st_add_background!.value || this.isSectionBg
    );
    this.backgroundBannerIcon = this.getBackgroundBannerIconColor(
      caasData.formData.st_badge_color!.value.identifier
    );
    this.headline = caasData.formData.st_header!.value;
    this.innerHTMLText = caasData.formData.st_bodytext!.value;
    this.imageSrc = caasData.formData.st_image?.value.url;
    if (caasData.formData.st_image?.value?.identifier) {
      this.imageAlt = this.imagesService.getAltTextById(
        caasData.formData.st_image.value.identifier
      );
    }
    if (caasData?.formData?.st_image_title?.value) {
      this.imageTitle = caasData.formData.st_image_title.value;
    }
    this.actionsType = this.getCrossReferencingActionType(
      caasData.formData.st_link,
      caasData.formData.st_link_type
    );
    if (
      caasData.formData.st_link?.value &&
      caasData.formData.st_downloads?.value?.length === 0
    ) {
      this.actionArray = this.getCrossReferencingCTALinks(
        caasData.formData.st_link.value,
        caasData.formData.st_link_optional?.value
      );
    } else if (
      caasData.formData.st_downloads &&
      caasData.formData.st_downloads.value.length > 0
    ) {
      this.actionArray = this.getCrossReferencingDownloadsLinks(
        caasData.formData.st_downloads
      );
    }
    this.buttonLayout = this.caasBodytextService.getButtonLayout(
      caasData.formData.st_link!,
      caasData.formData.st_link_type!
    );
    this.bannerIconName = caasData.formData.st_badge_icon!.value;
  }

  getBackgroundBannerIconColor(color: string): string {
    switch (color) {
      case 'blue':
        return 'blue';
      case 'turquoise':
        return 'light-blue';
      case 'grey':
        return 'grey';
      default:
        return 'green';
    }
  }

  getCrossReferencingActionType(
    link?: CaasLink,
    linkType?: CaasFormField
  ): ActionType {
    if (linkType?.value?.identifier === 'cta') {
      if (
        link?.value?.formData?.lt_button_layout?.value?.identifier ===
        'textlink'
      ) {
        return ActionType.LINK;
      } else {
        return ActionType.BUTTON;
      }
    } else {
      return ActionType.LINK;
    }
  }

  getCrossReferencingCTALinks(
    linkValue: CaasLink['value'],
    linkOptionalValue: CaasLinkOptional['value']
  ): Link[] {
    const ctaLinks: Link[] = [];
    ctaLinks.push(this.getCtaLink(linkValue, false));
    if (linkOptionalValue) {
      ctaLinks.push(this.getCtaLink(linkOptionalValue, true));
    }
    return ctaLinks;
  }

  getCrossReferencingDownloadsLinks(downloads: CaasDownloads): Link[] {
    return downloads?.value?.map((download) => {
      return {
        buttonLayout: LinkType.DOWNLOAD_NO_UNDERSCORED,
        label: download?.formData?.lt_link_label?.value ?? '',
        href: download?.formData?.lt_link_external?.value ?? '',
        hrefType: HrefType.EXTERNAL,
        title: download?.formData?.lt_link_title?.value ?? '',
      };
    });
  }

  getCtaLink(
    linkValue: CaasLink['value'] | CaasLinkOptional['value'],
    optionalLink: boolean
  ): Link {
    switch (linkValue?.formData?.lt_link_type?.value?.identifier) {
      case 'external':
      case 'download':
        return {
          label: linkValue?.formData?.lt_link_label?.value ?? '',
          title: linkValue?.formData?.lt_link_title?.value ?? '',
          href: linkValue?.formData?.lt_link_external?.value ?? '',
          hrefType: HrefType.EXTERNAL,
          buttonLayout: optionalLink
            ? this.checkLinksTypeOptional(
                linkValue as CaasLinkOptional['value']
              )
            : undefined,
        };
      case 'internal':
        return {
          label: linkValue?.formData?.lt_link_label?.value ?? '',
          title: linkValue?.formData?.lt_link_title?.value ?? '',
          href: linkValue?.formData?.lt_link_internal?.value
            ? (this.caasRoutesService.getSeoRouteByIdentifier(
                linkValue?.formData?.lt_link_internal?.value?.identifier ?? '',
                this.router.config
              )?.path ?? '404')
            : '/404',
          hrefType: HrefType.ANGULAR_ROUTE,
          fragment: linkValue?.formData?.st_anchor_name?.value ?? '',
          buttonLayout: optionalLink
            ? this.checkLinksTypeOptional(
                linkValue as CaasLinkOptional['value']
              )
            : undefined,
        };
      case 'video':
        return {
          label: linkValue?.formData?.lt_link_label?.value ?? '',
          title: linkValue?.formData?.lt_link_title?.value ?? '',
          hrefType: HrefType.OPEN_MODAL_VIDEO,
          video: {
            videoSrc: this.getVideoSrc(linkValue),
            videoId:
              (linkValue?.formData?.lt_link_video_type?.value?.identifier ===
              'vimeo'
                ? linkValue?.formData?.lt_link_vimeo_id?.value
                : linkValue?.formData?.lt_link_youtube_id?.value) ?? '',
            thumbnailSrc: '',
          },
          buttonLayout: optionalLink
            ? this.checkLinksTypeOptional(
                linkValue as CaasLinkOptional['value']
              )
            : undefined,
        };
      default:
        return {
          label: '',
          href: '',
          title: '',
          hrefType: HrefType.ANGULAR_ROUTE,
        };
    }
  }

  getVideoSrc(
    linkValue: CaasLink['value'] | CaasLinkOptional['value']
  ): SafeResourceUrl {
    switch (
      linkValue &&
      linkValue?.formData?.lt_link_video_type?.value?.identifier
    ) {
      case 'vimeo':
        return this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://player.vimeo.com/video/${linkValue?.formData?.lt_link_vimeo_id?.value}`
        );
      case 'youtube':
        return this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.youtube.com/embed/${linkValue?.formData?.lt_link_youtube_id?.value}`
        );
      default:
        return '';
    }
  }

  setBackgroundColor(background: boolean): Colors | undefined {
    const materialDownloadContainer =
      this.document.documentElement.querySelector(
        '.material-download-container'
      );
    if (materialDownloadContainer) {
      const backgroundColor = getComputedStyle(
        materialDownloadContainer
      ).backgroundColor;
      const hex = this.rgbToHex(backgroundColor);
      if (background) {
        return Colors.NEUTRAL_LIGHTER;
      } else {
        return hex as Colors;
      }
    } else {
      if (background) {
        return Colors.NEUTRAL_LIGHTEST;
      } else {
        return undefined;
      }
    }
  }

  private rgbToHex(rgb: string): string {
    // Extract r,g,b values from string like "rgb(255, 255, 255)" or "rgba(255, 255, 255, 1)"
    const values = rgb.match(/\d+/g);
    if (!values || values.length < 3) return '';

    // Convert each component to hex and pad with zeros if needed
    const r = parseInt(values[0]).toString(16).padStart(2, '0');
    const g = parseInt(values[1]).toString(16).padStart(2, '0');
    const b = parseInt(values[2]).toString(16).padStart(2, '0');

    return `#${r}${g}${b}`;
  }

  checkLinksTypeOptional(
    optionalLinkValue: CaasLinkOptional['value']
  ): LinkType {
    switch (optionalLinkValue?.formData?.lt_link_type?.value?.identifier) {
      case 'internal':
        return LinkType.PRIMARY_ARROW;
      case 'external':
        return LinkType.EXTERNAL;
      case 'video':
        return LinkType.VIDEO_LINK_NO_UNDERSCORE;
      case 'download':
        return LinkType.DOWNLOAD_NO_UNDERSCORED;
      default:
        return LinkType.PRIMARY_ARROW;
    }
  }
}
